import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { getDeviceFingerprint } from '../utils/fingerprint';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage cache for instant load
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user_cache');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [connectionError, setConnectionError] = useState(false);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);

  // Cache user data whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user_cache', JSON.stringify(user));
    }
  }, [user]);

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setConnectionError(false);
      retryCountRef.current = 0;
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        logout();
      } else {
        // Network error - don't logout, keep cached user
        setConnectionError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Auto-retry with proper exponential backoff using setTimeout
  useEffect(() => {
    if (connectionError && token) {
      const scheduleRetry = () => {
        const count = retryCountRef.current;
        // Backoff: 3s, 5s, 8s, 15s, 30s, then cap at 30s
        const delays = [3000, 5000, 8000, 15000, 30000];
        const delay = delays[Math.min(count, delays.length - 1)];
        retryTimerRef.current = setTimeout(async () => {
          retryCountRef.current += 1;
          await fetchUser();
          // If still in error state, schedule next retry
          // fetchUser sets connectionError which re-triggers this effect
        }, delay);
      };
      scheduleRetry();
    }
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [connectionError, token, fetchUser]);

  const retryConnection = () => {
    if (token) {
      retryCountRef.current = 0;
      setConnectionError(false);
      fetchUser();
    }
  };

  const login = async (email, password) => {
    const fp = await getDeviceFingerprint();
    const response = await api.post('/auth/login', { email, password, device_fingerprint: fp });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setConnectionError(false);
    return response.data;
  };

  const register = async (email, password, full_name, rut_passport, whatsapp) => {
    const fp = await getDeviceFingerprint();
    const response = await api.post('/auth/register', { email, password, full_name, rut_passport, whatsapp, device_fingerprint: fp });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_cache');
    setToken(null);
    setUser(null);
    setConnectionError(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, connectionError, login, register, logout, retryConnection, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};
